/**
 * KMS roster editor — the name list that powers /kmsleaderboard.
 * One name per line; matching against registrations is fuzzy
 * ("Maria G" finds "Maria Gonzalez").
 */
import { useCallback, useEffect, useState } from "react";
import { UsersThree, FloppyDisk, ArrowSquareOut } from "@phosphor-icons/react";
import CollapsibleSection from "@/components/admin/CollapsibleSection";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function KmsRosterPanel({ token }) {
  const [text, setText] = useState("");
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const [board, setBoard] = useState(null);

  const headers = { "X-Admin-Token": token };

  const load = useCallback(async () => {
    try {
      const [r, b] = await Promise.all([
        fetch(`${BACKEND}/api/admin/kms-roster`, { headers }),
        fetch(`${BACKEND}/api/sacrifice/kms-leaderboard`),
      ]);
      const d = await r.json();
      setText((d.names || []).join("\n"));
      setBoard(await b.json());
    } catch (e) {
      setStatus(e?.message || "Could not load roster");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true); setStatus("");
    try {
      const names = text.split("\n").map((n) => n.trim()).filter(Boolean);
      const r = await fetch(`${BACKEND}/api/admin/kms-roster`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ names }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setStatus(`Saved ${d.count} names`);
      load();
    } catch (e) {
      setStatus(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <CollapsibleSection
      id="kms-roster"
      testId="kms-roster-panel"
      title="KMS leaderboard roster"
      icon={<UsersThree size={20} weight="bold" className="text-[#FF1B91]" />}
      subtitle="Who appears on /kmsleaderboard — one name per line."
      onRefresh={load}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <a
            href="/kmsleaderboard"
            target="_blank"
            rel="noreferrer"
            data-testid="open-kms-board-link"
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold border border-white/25 text-white hover:border-[#FF1B91] px-4 py-2"
          >
            <ArrowSquareOut size={12} weight="bold" /> Open the board
          </a>
          {board && (
            <span className="text-[11px] text-white/55" data-testid="kms-roster-summary">
              <b className="text-white">{board.roster_size}</b> on the roster ·{" "}
              <b className="text-white">{board.registered_count}</b> matched to a signup ·{" "}
              <b className="text-white">{board.total_minutes?.toLocaleString()}</b> team minutes
            </span>
          )}
        </div>

        {board?.not_registered?.length > 0 && (
          <div className="text-[11px] text-yellow-200/80 border border-yellow-500/30 bg-yellow-500/[0.04] px-3 py-2" data-testid="kms-unmatched">
            <span className="text-[9px] uppercase tracking-[0.25em] font-bold text-yellow-300">Couldn't match to a registration: </span>
            {board.not_registered.join(" · ")}
            <div className="text-white/45 mt-1">
              These show on the board at 0 minutes. If they ARE signed up, their registered name is spelled
              differently — add a match hint on their line below (e.g. <span className="font-mono">Bill S | William Smith</span>)
              and their real minutes will appear.
            </div>
          </div>
        )}

        <div className="text-[11px] text-white/45 leading-relaxed border border-white/10 bg-black/20 px-3 py-2">
          <b className="text-white/70">Tip:</b> if someone registered under a different name, add a match hint with a
          pipe — e.g. <span className="font-mono text-white/70">Liv H | Olivia Hart</span>. The board shows the left
          side, and matches on the right. Use <span className="font-mono text-white/70">| @owner</span> for your own
          pinned record.
        </div>

        <textarea
          data-testid="kms-roster-textarea"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={16}
          spellCheck={false}
          className="w-full bg-black/50 border border-white/15 focus:border-[#FF1B91] outline-none px-3 py-2 text-sm text-white font-mono"
        />

        <div className="flex items-center gap-3">
          <button
            type="button"
            data-testid="save-kms-roster-btn"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold border border-[#FF1B91] text-white bg-[#FF1B91]/10 hover:bg-[#FF1B91]/25 disabled:opacity-40 px-4 py-2"
          >
            <FloppyDisk size={12} weight="bold" /> {saving ? "Saving…" : "Save roster"}
          </button>
          {status && <span className="text-[11px] text-emerald-300" data-testid="kms-roster-status">{status}</span>}
        </div>
      </div>
    </CollapsibleSection>
  );
}
